<template>
  <div class="upload-item" :class="{ 'upload-item--complete': isComplete }">
    <div class="upload-item-content">
      <img
          loading="lazy"
          :src="fileIcon"
          class="file-icon"
          :alt="`${file.name} icon`"
      />
      <div class="file-details">
        <div class="file-info">
          <span class="file-name">{{ file.name }}</span>
          <span class="file-size">{{ formattedSize }}</span>
        </div>
        <div class="progress-wrapper">
          <div class="progress-track">
            <div
                class="progress-fill"
                :style="{ width: `${progress}%` }"
            ></div>
          </div>
          <span class="progress-percentage">{{ progress }}%</span>
        </div>
      </div>
    </div>
    <button
        type="button"
        class="remove-button"
        :aria-label="`Remove ${file.name}`"
        @click="$emit('remove')"
    >
      <img
          v-if="progress==100"
          loading="lazy"
          src="/assets/tik.svg"
          class="remove-icon"
          alt=""
      />
    </button>
  </div>
</template>

<script lang="ts" setup>
import {computed} from 'vue';
import type {PropType} from 'vue';
import type {FileUpload} from '../types';

const emits = defineEmits(['remove']);
const props = defineProps({
  upload: {
    type: Object as PropType<FileUpload>,
    required: true
  },
  userId: {
    type: Number,
    required: false
  }
});

const progress = computed(() => props.upload.progress);
const formattedSize = computed(() => {
  const bytes = props.upload.file!.size;
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
});

const isComplete = computed(() => props.upload.status === 'complete');
const file = computed(() => props.upload.file!);
const fileIcon = computed(() => `/assets/file.svg`);

</script>

<style scoped>
.upload-item {
  border-radius: 8px;
  border: 1px solid var(--Neutral-7, #787a87);
  background-color: #fff;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 16px;
  margin-bottom: 8px;
}

.upload-item--complete {
  border-color: var(--Primary-Primary, #080aed);
}

.upload-item-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
}

.file-icon {
  width: 28px;
  height: 28px;
}

.file-details {
  flex: 1;
  min-width: 240px;
}

.file-info {
  display: flex;
  flex-direction: column;
}

.file-name {
  color: var(--Neutral-5, #191a1f);
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
}

.file-size {
  color: var(--Neutral-7, #787a87);
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  margin-top: 4px;
}

.progress-wrapper {
  display: flex;
  margin-top: 8px;
  align-items: center;
  gap: 12px;
}

.progress-track {
  flex: 1;
  min-width: 240px;
  border-radius: 4px;
  background: var(--Neutral-6, #c7c8cc);
  height: 8px;
}

.progress-fill {
  border-radius: 4px;
  background: var(--Primary-Primary, #080aed);
  height: 100%;
  transition: width 0.3s ease;
}

.progress-percentage {
  color: var(--Neutral-5, #191a1f);
  font: 500 12px/1 Montserrat, sans-serif;
  min-width: 48px;
  text-align: right;
}

.remove-button {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-button:hover {
  opacity: 0.8;
}

.remove-icon {
  width: 16px;
  height: 16px;
}
</style>